import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'walletUserId';

interface AuthApi {
  /** Signed-in account id, or null. The one gate the whole app checks. */
  meId: string | null;
  signIn: (accountId: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthApi | null>(null);

/**
 * Who is signed in, for the whole app - not just the wallet.
 *
 * Every page shares this so the nav tabs and the other pages can refuse to
 * be useful before sign-in, not only the wallet's own landing view.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [meId, setMeId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  const signIn = useCallback((accountId: string) => {
    localStorage.setItem(STORAGE_KEY, accountId);
    setMeId(accountId);
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setMeId(null);
  }, []);

  const value = useMemo<AuthApi>(() => ({ meId, signIn, signOut }), [meId, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
